import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../data/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    const link = NAV_LINKS.find((l) => l.id === id);
    // Links with an explicit route navigate there
    if (link?.route) {
      navigate(link.route);
      return;
    }
    // Menu is its own route
    if (id === "menu") {
      navigate("/menu");
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#FAF3E7]/85 border-b border-[#E8D8C0] py-3 shadow-sm"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button
          onClick={() => {
            if (location.pathname !== "/") navigate("/");
            else
              document
                .getElementById("home")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src={BRAND.logoTransparent}
            alt="Andhra Biryani Co"
            className="h-12 w-12 rounded-full object-cover ring-1 ring-[#C8A14B]/60 bg-[#3A1B0E]"
          />
          <div className="hidden sm:flex flex-col items-start leading-none">
            <span className="font-display text-lg text-cocoa tracking-tight leading-none">
              Andhra Biryani Co
            </span>
            <span className="mt-1 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.22em] text-gold font-semibold">
              <span className="inline-block h-px w-3 bg-[#C8A14B]/70" />
              Andhra Style · Authentic Taste
            </span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => goToSection(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm text-cocoa-soft hover:text-cocoa transition-colors relative group"
            >
              {l.label}
              <span className="absolute left-0 -bottom-1 h-px w-0 bg-gold group-hover:w-full transition-all duration-300" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/order/jubilee")}
            data-testid="nav-cta-order"
            className="hidden md:inline-flex items-center rounded-full bg-[#C0392B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#D03A2A] hover:-translate-y-0.5 transition-all shadow-md"
          >
            Order Now
          </button>
          <button
            onClick={() => setOpen((s) => !s)}
            className="lg:hidden p-2 rounded-md text-cocoa"
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-[#E8D8C0] bg-[#FAF3E7]"
          data-testid="nav-mobile-panel"
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                onClick={() => goToSection(l.id)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="text-left py-3 text-cocoa-soft hover:text-cocoa border-b border-[#E8D8C0] last:border-b-0"
              >
                {l.label}
              </button>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                navigate("/order/jubilee");
              }}
              data-testid="nav-mobile-cta"
              className="mt-3 rounded-full bg-[#C0392B] text-white px-5 py-3 text-sm font-semibold"
            >
              Order Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
